import { Box, Dialog, DialogActions, DialogContent, Divider, Typography } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { PermissionActionType } from "@/constants/permissions"
import type { GlTransactionRow, SignedUrl } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface TransactionDocumentsDialogProps {
  row: GlTransactionRow
  onClose: () => void
  onDocumentDeleted?: () => void
}

export const TransactionDocumentsDialog: FC<TransactionDocumentsDialogProps> = ({
  row,
  onClose,
  onDocumentDeleted,
}) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [pendingFileId, setPendingFileId] = useState<string | null>(null)
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = hasPermissions([PermissionActionType.GL_TRANSACTION_MAINTENANCE])

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: row.glTransactionId,
    referenceTableName: ReferenceTableName.GlTransaction,
  })

  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  const handleDeleteRequest = (document: SignedUrl) => {
    setPendingFileId(document.file?.fileId ?? null)
    setIsConfirmationOpen(true)
  }

  const columns = useFileColumns({
    canDelete: hasMaintenancePermission,
    onDelete: handleDeleteRequest,
  })

  const handleDeleteConfirm = async () => {
    if (!pendingFileId) return
    const success = await removeSingleFile(pendingFileId)
    if (success) {
      notify({ type: NotificationType.Success, message: "Document removed" })
      onDocumentDeleted?.()
    } else {
      notify({ type: NotificationType.Error, message: "Failed to remove document. Please try again." })
    }
    setPendingFileId(null)
    setIsConfirmationOpen(false)
  }

  const transactionDescription = row.glTransaction?.description ?? ""
  const referenceNumber = row.glTransaction?.referenceNumber ?? ""

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='transaction.documents-dialog'>
        <DialogHeader title='Documents' onClose={onClose} />
        <DialogContent>
          <Box className='flex flex-col gap-6 px-5 py-3'>
            <Box className='flex flex-col gap-3'>
              <Typography className='text-base font-bold text-gray-700'>{transactionDescription}</Typography>
              {referenceNumber && (
                <Box className='flex items-start gap-10'>
                  <Box className='flex flex-col gap-1'>
                    <Typography className='text-sm font-bold text-gray-700'>Reference Number</Typography>
                    <Typography variant='body2' className='text-gray-700'>
                      {referenceNumber}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box className='flex flex-col gap-2.5'>
              <Typography className='text-base font-medium text-gray-700'>View Documents</Typography>
              <Divider />
            </Box>

            <FilesTable data={fileList} isLoading={fileFetching} columns={columns} />
          </Box>
        </DialogContent>

        <DialogActions className='flex items-center justify-end px-5 pb-5'>
          <Button appearance='contained' onClick={onClose} data-testid='transaction.documents-dialog.close-button'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {isConfirmationOpen && (
        <ConfirmationDialog
          title='Remove Document'
          content='Are you sure you want to remove this document? This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Remove'
          onClose={() => {
            setPendingFileId(null)
            setIsConfirmationOpen(false)
          }}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  )
}
